import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { LoadingState } from "@/components/base/LoadingState";
import { StatusBadge } from "@/components/base/StatusBadge";
import { Modal } from "@/components/base/Modal";
import { canManageListings } from "@/lib/permissions";
import {
  type ChannelListing,
  type ReadinessResult,
  type ChannelListingVariant,
  type ListingException,
  type DirtyDomain,
  type PriceStrategy,
  evaluateReadiness,
  archiveChannelListing,
  requestPublication,
  approveListingVersion,
  getPublicationProjection,
  isInheritingContent,
  isInheritingPrice,
  canPublish,
  canEdit,
  canEnd,
  hasDirtyDomain,
  readinessLabel,
  readinessTone,
  readinessAction,
  readinessCheckHref,
  readinessConnectorLine,
  approvalState,
} from "@/lib/channels";
import { connectorIsReady } from "@/lib/channel-coverage";
import { connectorDisplayName, connectorAuthLabel, formatConnectorIdentityLine } from "@/lib/connector-identity";
import { computeChannelPrice, pricingConfigFromProviderConfig, publicationStateMeta, storedPriceFormState, priceFormConfig, formatPriceAdjustmentLine } from "@/lib/listing-pricing";
import {
  formatStoredMoney,
  listingPriceRowsFromLocalState,
  summarizeListingChannelPrices,
  variantChannelPriceKindLabel,
  resolveVariantEffectiveChannelPrice,
} from "@/lib/listing-channel-price-display";
import { listingVariantChannelPriceRpcArgs, mergeChannelPriceOverlay } from "@/lib/listing-variant-channel-price-control";
import { VariantChannelPriceControl } from "@/pages/listings/detail/components/VariantChannelPriceControl";
import { effectiveSellingPrice } from "@/lib/products";
import { currencySymbol } from "@/lib/currency";
import { indexConnectorStorefront, listingLiveLinks } from "@/lib/listing-live-display";
import { canonicalListingProductId, listingSyncHistoryBadge, operatorDirtyDomains, shouldShowSyncChanges, waitingToSyncCopy } from "@/lib/phase1-sync-contract";
import {
  approvalPendingSummary,
  buildListingApprovalReview,
  lastFullPublishLabel,
  lastSyncLabel,
} from "@/lib/listing-approval-semantics";
import { syncListingDomains } from "@/lib/api/listing-sync";
import { ViewLiveLink, AdminListingLink } from "@/components/feature/ViewLiveLink";
import { formatMoneyPreview } from "@/lib/money-entry";
import EbayTab from "@/pages/listings/detail/components/EbayTab";
import BigcommerceTab from "@/pages/listings/detail/components/BigcommerceTab";
import { ListingMediaPanel } from "@/pages/listings/detail/components/ListingMediaPanel";
import ListingTemplateOverride from "@/pages/listing-templates/components/ListingTemplateOverride";
import RealListingPreview from "@/pages/listing-templates/components/RealListingPreview";
import PublicationGateNotice from "@/components/feature/PublicationGateNotice";
import {
  lostDetailNote,
  publicationJobDiagnostic,
  publicationNoticeModel,
} from "@/lib/listing-publication-gate";

const dirtyLabel: Record<DirtyDomain, string> = {
  content: "Content",
  variants: "Variants",
  pricing: "Pricing",
  inventory: "Inventory",
  media: "Media",
  attributes: "Attributes",
  category: "Category",
  seo: "SEO",
  shipping: "Shipping",
};

type ContentField =
  | "title"
  | "description"
  | "short_description"
  | "subtitle"
  | "condition_description";

export default function ListingDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"overview" | "variants" | "content" | "pricing" | "inventory" | "media" | "readiness" | "sync" | "ebay" | "bigcommerce" | "preview">("overview");
  const [variantPage, setVariantPage] = useState(0);
  const [variantPageSize] = useState(50);
  const [readinessData, setReadinessData] = useState<ReadinessResult | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [projection, setProjection] = useState<Record<string, unknown> | null>(null);
  const [projectionLoading, setProjectionLoading] = useState(false);
  const [priceRule, setPriceRule] = useState<PriceStrategy>(() => storedPriceFormState(null, {}).rule);
  const [priceValue, setPriceValue] = useState("");
  const [channelPriceOverlay, setChannelPriceOverlay] = useState<Record<string, number | null>>({});
  const [contentEdit, setContentEdit] = useState<{ field: ContentField; value: string } | null>(null);

  const canManage = canManageListings(role);

  const selectTab = (tab: typeof activeTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next);
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["listing-detail", org?.id, id, variantPage],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const { data: listingData } = await supabase
        .from("channel_listings")
        .select(
          "*, connector:connectors(id, name, provider, auth_status, status, capabilities), product:products(id, name, sku, description, selling_price, cost_price, brand, manufacturer, model_number, mpn, country_of_origin, hs_tariff_code, weight_kg, length_cm, width_cm, height_cm, condition, product_type)"
        )
        .eq("org_id", org!.id)
        .eq("id", id)
        .maybeSingle();

      const listing = listingData as unknown as ChannelListing | null;

      const [variantsRes, priceVariantsRes, media, readiness, exceptions, attributeValues, bcRow, gallery, bcConfig, historyRes] = await Promise.all([
        supabase
          .from("channel_listing_variants")
          .select("*, variant:variants(id, sku, barcode, option_values, selling_price, cost_price, status)")
          .eq("org_id", org!.id)
          .eq("channel_listing_id", id)
          .order("created_at")
          .range(variantPage * variantPageSize, variantPage * variantPageSize + variantPageSize - 1),
        supabase
          .from("channel_listing_variants")
          .select("id, variant_id, included, channel_sku, channel_price, variants(id, sku, option_values, selling_price)")
          .eq("org_id", org!.id)
          .eq("channel_listing_id", id)
          .eq("included", true),
        supabase.rpc("get_effective_listing_media", { p_org_id: org!.id, p_listing_id: id! }),
        supabase
          .from("listing_readiness_checks")
          .select("*")
          .eq("org_id", org!.id)
          .eq("channel_listing_id", id)
          .maybeSingle(),
        supabase
          .from("listing_exceptions")
          .select("*")
          .eq("org_id", org!.id)
          .eq("channel_listing_id", id)
          .eq("resolved", false)
          .order("last_seen_at", { ascending: false }),
        supabase
          .from("listing_attribute_values")
          .select("*")
          .eq("org_id", org!.id)
          .eq("channel_listing_id", id),
        supabase.from("bigcommerce_products").select("external_product_id, storefront_custom_url").eq("org_id", org!.id).eq("listing_id", id).maybeSingle(),
        supabase.from("product_media").select("media_asset_id, is_primary, media_assets(original_filename)").eq("org_id", org!.id).eq("product_id", listing?.product_id ?? "").eq("status", "active"),
        listing?.connector_id
          ? supabase.from("connector_bigcommerce_config").select("connector_id, storefront_origin, store_hash").eq("org_id", org!.id).eq("connector_id", listing.connector_id).maybeSingle()
          : Promise.resolve({ data: null }),
        listing?.product_id
          ? supabase
            .from("product_history")
            .select("field, old_value, new_value, created_at")
            .eq("org_id", org!.id)
            .eq("product_id", listing.product_id)
            .gt("created_at", listing.approved_at ?? listing.created_at)
            .order("created_at", { ascending: false })
            .limit(80)
          : Promise.resolve({ data: [] }),
      ]);

      return {
        listing,
        variants: (variantsRes.data ?? []) as unknown as ChannelListingVariant[],
        priceVariants: (priceVariantsRes.data ?? []) as unknown as ChannelListingVariant[],
        media: (media.data ?? []) as any[],
        readiness: readiness.data
          ? ({
              status: readiness.data.status,
              blocking: readiness.data.blocking_count ?? 0,
              warning: readiness.data.warning_count ?? 0,
              checks: readiness.data.checks ?? [],
            } as ReadinessResult)
          : null,
        exceptions: (exceptions.data ?? []) as unknown as ListingException[],
        attributeValues: attributeValues.data ?? [],
        confirmedId: (bcRow.data as { external_product_id?: number | null } | null)?.external_product_id
          ? String((bcRow.data as { external_product_id: number }).external_product_id)
          : listing?.external_listing_id ?? null,
        storefrontCustomUrl: (bcRow.data as { storefront_custom_url?: string | null } | null)?.storefront_custom_url ?? null,
        gallery: (gallery.data ?? []) as { media_asset_id: string; is_primary: boolean; media_assets: { original_filename: string | null } | { original_filename: string | null }[] | null }[],
        storefront: indexConnectorStorefront(
          bcConfig.data
            ? [bcConfig.data as { connector_id: string; storefront_origin: string | null; store_hash: string | null }]
            : [],
        ),
        history: (historyRes.data ?? []) as { field: string; old_value: string | null; new_value: string | null; created_at: string }[],
      };
    },
  });

  useEffect(() => {
    if (!data?.listing) return;
    const stored = storedPriceFormState(
      data.listing.price_strategy,
      pricingConfigFromProviderConfig(data.listing.provider_config),
    );
    setPriceRule(stored.rule);
    setPriceValue(stored.amount);
    // Hydrate from stored listing identity/strategy only so opening Pricing does not reset drafts.
  }, [data?.listing?.id, data?.listing?.price_strategy, data?.listing?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setChannelPriceOverlay({});
  }, [id]);

  useEffect(() => {
    const rows = data?.priceVariants;
    if (!rows?.length) return;
    setChannelPriceOverlay((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      for (const row of rows) {
        const nestedRaw = (row as { variant?: { id?: string }; variants?: { id?: string } | { id?: string }[] }).variant
          ?? (row as { variants?: { id?: string } | { id?: string }[] }).variants
          ?? null;
        const nested = Array.isArray(nestedRaw) ? nestedRaw[0] ?? null : nestedRaw;
        const variantId = String(nested?.id || (row as { variant_id?: string }).variant_id || "");
        if (!variantId || !(variantId in next)) continue;
        const server = (row as { channel_price?: number | null }).channel_price ?? null;
        const local = next[variantId];
        const same =
          (server == null && local == null) ||
          (server != null && local != null && Number(server) === Number(local));
        if (same) {
          delete next[variantId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [data?.priceVariants, data?.listing?.updated_at]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (
      tab === "overview" ||
      tab === "variants" ||
      tab === "content" ||
      tab === "pricing" ||
      tab === "inventory" ||
      tab === "media" ||
      tab === "readiness" ||
      tab === "sync" ||
      tab === "ebay" ||
      tab === "bigcommerce" ||
      tab === "preview"
    ) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleEvaluate = async () => {
    if (!id) return;
    setReadinessLoading(true);
    try {
      const result = await evaluateReadiness(org!.id, id);
      setReadinessData(result);
    } catch {
      setReadinessData({ status: "BLOCKED", blocking: 1, warning: 0, checks: [{ type: "blocking", code: "evaluation_failed", message: "Could not evaluate readiness." }] });
    }
    setReadinessLoading(false);
  };

  const handlePublish = async () => {
    if (!id) return;
    setActionBusy(true);
    setActionError("");
    try {
      const result = await requestPublication(org!.id, id);
      if (!result.success) {
        setActionError(result.error || "Publication request failed");
      } else {
        queryClient.invalidateQueries({ queryKey: ["listing-detail", org?.id, id, variantPage] });
      }
    } catch (err) {
      setActionError((err as Error).message);
    }
    setActionBusy(false);
  };

  const handleArchive = async () => {
    if (!id) return;
    setActionBusy(true);
    try {
      await archiveChannelListing(org!.id, id);
      setArchiveConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["listing-detail", org?.id, id, variantPage] });
    } catch (err) {
      setActionError((err as Error).message);
    }
    setActionBusy(false);
  };

  const handleProjection = async () => {
    if (!id || !org?.id) return;
    setProjectionLoading(true);
    setActionError("");
    try {
      const proj = await getPublicationProjection(org.id, id);
      if (!proj) throw new Error("Publication projection is unavailable.");
      setProjection(proj as unknown as Record<string, unknown>);
    } catch (err) {
      setProjection(null);
      setActionError(err instanceof Error ? err.message : "Could not load publication projection.");
    } finally {
      setProjectionLoading(false);
    }
  };

  const handleListingVariantChannelPrice = async (variantId: string, price: number | null) => {
    if (!org?.id || !id) throw new Error("Listing not found");
    setActionBusy(true);
    setActionError("");
    try {
      const { error: rpcErr } = await supabase.rpc(
        "set_listing_variant_channel_price",
        listingVariantChannelPriceRpcArgs({
          orgId: org.id,
          listingId: id,
          variantId,
          price,
        }),
      );
      if (rpcErr) throw new Error(rpcErr.message);
      setChannelPriceOverlay((prev) => ({ ...prev, [variantId]: price }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["listing-detail", org.id, id] }),
        queryClient.invalidateQueries({ queryKey: ["listings-index", org.id] }),
      ]);
    } finally {
      setActionBusy(false);
    }
  };

  const saveContentOverride = async () => {
    if (!org?.id || !id || !contentEdit) return;
    setActionBusy(true);
    setActionError("");
    try {
      const { data: result, error: rpcErr } = await supabase.rpc("set_listing_content_override", {
        p_org_id: org.id,
        p_listing_id: id,
        p_field: contentEdit.field,
        p_value: contentEdit.value,
      });
      if (rpcErr) throw new Error(rpcErr.message);
      const payload = result as unknown as { success?: boolean; error?: string } | null;
      if (payload?.success !== true) throw new Error(payload?.error || "Could not save content override");
      setContentEdit(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["listing-detail", org.id, id] }),
        queryClient.invalidateQueries({ queryKey: ["listings-index", org.id] }),
      ]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not save content override");
    } finally {
      setActionBusy(false);
    }
  };

  const resetContentToInherited = async (field: ContentField) => {
    if (!org?.id || !id) return;
    setActionBusy(true);
    setActionError("");
    try {
      const { data: result, error: rpcErr } = await supabase.rpc("reset_listing_field_to_inherit", {
        p_org_id: org.id,
        p_listing_id: id,
        p_field: field,
      });
      if (rpcErr) throw new Error(rpcErr.message);
      const payload = result as unknown as { success?: boolean; error?: string } | null;
      if (payload?.success !== true) throw new Error(payload?.error || "Could not restore inherited content");
      setContentEdit(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["listing-detail", org.id, id] }),
        queryClient.invalidateQueries({ queryKey: ["listings-index", org.id] }),
      ]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not restore inherited content");
    } finally {
      setActionBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    setActionBusy(true);
    setActionError("");
    try {
      const result = await approveListingVersion(org!.id, id);
      if (!result.success) {
        setActionError(result.error || "Approval failed");
      } else {
        queryClient.invalidateQueries({ queryKey: ["listing-detail", org?.id, id, variantPage] });
      }
    } catch (err) {
      setActionError((err as Error).message);
    }
    setActionBusy(false);
  };

  if (isLoading) return <LoadingState />;
  if (isError || !data?.listing) {
    return (
      <div>
        <PageHeader title="Listing" backTo="/listings" />
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500">Listing not found.</p>
          <button onClick={() => refetch()} className="mt-3 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      </div>
    );
  }

  const l = data.listing;
  const p = l.product as any;
  const productMasterId = canonicalListingProductId({ product_id: l.product_id, product: p });
  const connector = l.connector as any;
  const dirty = l.dirty_domains ?? [];
  const priceVariantSource = mergeChannelPriceOverlay(
    (data.priceVariants ?? data.variants ?? []) as Array<{
      variant_id?: string;
      included?: boolean;
      channel_price?: number | null;
      variant?: ChannelListingVariant["variant"];
      variants?: ChannelListingVariant["variant"];
    }>,
    channelPriceOverlay,
  );
  const priceRows = listingPriceRowsFromLocalState({
    priceStrategy: l.price_strategy,
    providerConfig: l.provider_config,
    productSellingPrice: p?.selling_price ?? null,
    variants: priceVariantSource,
  });
  const channelPriceSummary = summarizeListingChannelPrices({
    rows: priceRows,
    pricingDirty: dirty.includes("pricing") || Object.keys(channelPriceOverlay).length > 0,
    currency: l.currency,
  });
  const operatorDirty = operatorDirtyDomains(dirty);
  const isEbay = connector?.provider === "ebay";
  const isBigcommerce = connector?.provider === "bigcommerce";
  const connectionReady = connectorIsReady(connector?.auth_status);
  const approval = approvalState(l);
  const review = buildListingApprovalReview({ dirtyDomains: dirty, history: data.history ?? [] });
  /**
   * Effective publication eligibility. Readiness alone never authorizes
   * publication; an unresolved remote outcome blocks a new request.
   */
  const gateModel = publicationNoticeModel({
    status: l.status,
    publicationStatus: l.publication_status,
    connectionReady,
    approval,
    readinessStatus: data.readiness?.status ?? null,
    readinessBlocking: data.readiness?.blocking ?? 0,
    approvedVersion: l.approved_version,
  });
  const gate = gateModel.gate;

  const tabs = [
    { key: "overview" as const, label: "Overview", icon: "ri-file-list-line" },
    { key: "variants" as const, label: "Variants", icon: "ri-grid-line" },
    { key: "content" as const, label: "Content", icon: "ri-article-line" },
    { key: "pricing" as const, label: "Pricing", icon: "ri-price-tag-3-line" },
    { key: "inventory" as const, label: "Inventory", icon: "ri-archive-drawer-line" },
    { key: "media" as const, label: "Media", icon: "ri-image-line" },
    { key: "readiness" as const, label: "Readiness", icon: "ri-shield-check-line" },
    { key: "sync" as const, label: "Sync History", icon: "ri-refresh-line" },
    ...(isEbay ? [{ key: "ebay" as const, label: "eBay", icon: "ri-store-2-line" }] : []),
    ...(isBigcommerce ? [{ key: "bigcommerce" as const, label: "BigCommerce", icon: "ri-shopping-bag-3-line" }] : []),
    { key: "preview" as const, label: "Preview", icon: "ri-eye-line" },
  ];

  return (
    <div>
      <PageHeader title={l.channel_title || p?.name || "Listing"} backTo="/listings">
        {canManage && shouldShowSyncChanges(dirty) && (
          <button
            onClick={async () => {
              setActionBusy(true);
              setActionError("");
              const result = await syncListingDomains({ orgId: org!.id, listingId: id, domains: dirty.filter((d) => d !== "pricing" && d !== "inventory") });
              setActionBusy(false);
              if (!result.success) setActionError(result.error || "Sync failed — Retry");
              else queryClient.invalidateQueries({ queryKey: ["listing-detail", org?.id, id, variantPage] });
            }}
            disabled={actionBusy}
            className="px-4 py-2 bg-white border border-emerald-200 hover:bg-emerald-50 disabled:opacity-60 text-emerald-800 text-sm font-medium rounded-lg whitespace-nowrap"
          >
            {actionBusy ? "Syncing..." : "Sync changes"}
          </button>
        )}
        {canManage && canPublish(l.status) && gate.allowed && (
          <button onClick={handlePublish} disabled={actionBusy}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
            <i className="ri-store-2-line" /> {actionBusy ? "Requesting..." : "Publish"}
          </button>
        )}
        {canManage && canPublish(l.status) && !gate.allowed && connectionReady && (
          <span
            data-testid="listing-publish-blocked"
            className={`px-3 py-2 border text-xs font-medium rounded-lg whitespace-nowrap ${gate.tone === "bad" ? "bg-red-50 border-red-100 text-red-700" : "bg-slate-100 border-slate-200 text-slate-500"}`}
            title={gate.message}
          >
            <i className="ri-lock-line mr-1" /> {gate.label}
          </span>
        )}
        {canManage && canPublish(l.status) && !gate.allowed && !connectionReady && (
          <span className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium rounded-lg whitespace-nowrap">
            {formatConnectorIdentityLine({
              id: connector?.id ?? l.connector_id,
              name: connector?.name ?? "",
              provider: connector?.provider ?? null,
              authStatus: connector?.auth_status,
              storefrontOrigin: data.storefront.originByConnector[l.connector_id],
              storeHash: data.storefront.storeHashByConnector[l.connector_id],
            })}
          </span>
        )}
        {canManage && canEdit(l.status) && (approval === "none" || (approval === "stale" && review.requiresApproval)) && (
          <button onClick={handleApprove} disabled={actionBusy}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
            <i className="ri-check-double-line" /> {actionBusy ? "Approving..." : approval === "none" ? "Approve listing" : "Approve catalogue changes"}
          </button>
        )}
        {canManage && canEdit(l.status) && (
          <button onClick={handleProjection} disabled={projectionLoading}
            className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
            <i className={projectionLoading ? "ri-loader-4-line animate-spin" : "ri-eye-line"} /> {projectionLoading ? "Loading..." : "Projection"}
          </button>
        )}
        {canManage && canEnd(l.status) && (
          <button onClick={() => setArchiveConfirm(true)}
            className="px-4 py-2 bg-white border border-slate-200 hover:bg-red-50 hover:text-red-600 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
            End / Archive
          </button>
        )}
      </PageHeader>

      {actionError && (
        <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{actionError}</div>
      )}

      <div className="mb-4">
        <PublicationGateNotice model={gateModel} variant="full" />
      </div>

      {/* Info Bar */}
      <div className="mb-6 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={l.status} />
          <StatusBadge
            status={l.publication_status === "published" || Boolean(data.confirmedId) ? "success" : l.publication_status === "sync_failed" ? "error" : "pending"}
            customLabel={publicationStateMeta({
              publicationStatus: l.publication_status,
              status: l.status,
              hasDirty: operatorDirty.length > 0,
              connectionReady,
              lastError: l.last_error,
              providerConfirmedListed: Boolean(data.confirmedId),
            }).label}
          />
          {(() => {
            const links = listingLiveLinks({
              provider: connector?.provider,
              externalId: data.confirmedId,
              storefrontOrigin: data.storefront.originByConnector[l.connector_id],
              storeHash: data.storefront.storeHashByConnector[l.connector_id],
              customUrl: data.storefrontCustomUrl,
            });
            return (
              <>
                <ViewLiveLink href={links.liveUrl} />
                <AdminListingLink href={links.adminUrl} />
              </>
            );
          })()}
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${connectionReady ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
            {connectorAuthLabel(connector?.auth_status)}
          </span>
          {data.readiness && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              readinessTone(data.readiness.status) === "ready" ? "bg-emerald-50 text-emerald-700" :
              readinessTone(data.readiness.status) === "warning" ? "bg-amber-50 text-amber-700" :
              "bg-red-50 text-red-700"
            }`}>
              {readinessLabel(data.readiness.status)}
            </span>
          )}
          {dirty.length > 0 && (
            <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">
              Dirty: {dirty.map((d) => dirtyLabel[d]).join(", ")}
            </span>
          )}
          <span className="text-xs text-slate-500">{connectorDisplayName({
            id: connector?.id ?? l.connector_id,
            name: connector?.name ?? "",
            provider: connector?.provider ?? null,
            authStatus: connector?.auth_status,
            storefrontOrigin: data.storefront.originByConnector[l.connector_id],
            storeHash: data.storefront.storeHashByConnector[l.connector_id],
          })}</span>
          {productMasterId ? (
            <Link to={`/products/${productMasterId}`} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">{p?.name ?? "Product"}</Link>
          ) : (
            <span className="text-xs text-slate-400">Product Master unavailable</span>
          )}
          {approval === "approved" && (
            <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">Approved v{l.approved_version}</span>
          )}
          {approval === "stale" && (
            <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">
              Approval stale{approvalPendingSummary(dirty) ? ` · ${approvalPendingSummary(dirty)}` : ""}
            </span>
          )}
          {approval === "none" && (
            <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Not approved</span>
          )}
          <span className="text-xs text-slate-400">
            {lastFullPublishLabel()} {l.last_published_at ? new Date(l.last_published_at).toLocaleDateString() : "—"}
            {" · "}
            {lastSyncLabel()} {l.last_synced_at ? new Date(l.last_synced_at).toLocaleString() : "—"}
          </span>
          <span className="text-xs text-slate-400">catalogue v{l.intended_version} · pv{l.published_version}</span>
          {l.last_error && <span className="text-xs text-red-600">{l.last_error}</span>}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 overflow-x-auto">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit flex-wrap">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => selectTab(t.key)}
              className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap flex items-center gap-2 transition-colors ${
                activeTab === t.key ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}>
              <i className={t.icon} />
              {t.label}
              {t.key === "readiness" && data.readiness?.status === "BLOCKED" && (
                <span className="w-2 h-2 rounded-full bg-red-500" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Overview */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Content</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-400">Title</p>
                  <p className="text-sm text-slate-800">{l.channel_title || p?.name || "—"}</p>
                  {isInheritingContent(l, "title") && <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Inherited</span>}
                  {hasDirtyDomain(l, "content") && <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full ml-1">Dirty</span>}
                </div>
                <div>
                  <p className="text-xs text-slate-400">Description</p>
                  <p className="text-sm text-slate-600 line-clamp-3">{l.channel_description || p?.description || "—"}</p>
                  {isInheritingContent(l, "description") && <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Inherited</span>}
                </div>
                {l.channel_category_name || l.channel_category_id ? (
                  <div>
                    <p className="text-xs text-slate-400">Category</p>
                    <p className="text-sm text-slate-800">{l.channel_category_name || "Category selected"}</p>
                    <p className="text-xs text-slate-400">Provider ID stored internally</p>
                  </div>
                ) : null}
              </div>
            </div>

            {(approval === "stale" || review.rows.length > 0) && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Review changes</h3>
                {review.rows.length === 0 ? (
                  <p className="text-sm text-slate-500">No operator-reviewable catalogue changes since approval. Automatic price and inventory sync does not require approval.</p>
                ) : (
                  <>
                    {approval === "approved" && (
                      <p className="text-xs text-slate-500 mb-3">
                        These domains are waiting for a catalogue sync. Approval v{l.approved_version} already covers them, so they are not an unapproved change.
                      </p>
                    )}
                    <div className="space-y-3">
                      {review.rows.map((row, i) => (
                        <div key={`${row.domain}-${i}`} className="text-sm">
                          <p className="font-medium text-slate-800">{row.domainLabel}</p>
                          <p className="text-slate-600">{row.from} → {row.to}</p>
                          <p className={`text-xs ${row.requiresApproval ? "text-amber-700" : "text-slate-400"}`}>{row.note}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {data.exceptions.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Active Exceptions ({data.exceptions.length})</h3>
                <div className="space-y-2">
                  {data.exceptions.map((ex) => (
                    <div key={ex.id} className={`p-3 rounded-lg ${ex.severity === "blocking" ? "bg-red-50" : ex.severity === "warning" ? "bg-amber-50" : "bg-slate-50"}`}>
                      <p className={`text-sm font-medium ${ex.severity === "blocking" ? "text-red-700" : ex.severity === "warning" ? "text-amber-700" : "text-slate-700"}`}>
                        {ex.message}
                      </p>
                      <p className="text-xs text-slate-400">
                        {ex.exception_type} · {ex.occurrence_count > 1 ? `${ex.occurrence_count}x · ` : ""}
                        {new Date(ex.last_seen_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <SyncHistoryTab orgId={org!.id} listingId={id!} />

          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Product Master</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Name</span><span className="text-slate-800">{p?.name ?? "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">SKU</span><span className="text-slate-800 font-mono">{p?.sku ?? "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Brand</span><span className="text-slate-800">{p?.brand ?? "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Price</span><span className="text-slate-800">{formatStoredMoney(p?.selling_price, l.currency)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Condition</span><span className="text-slate-800">{p?.condition ?? "—"}</span></div>
              </div>
            </div>

            <ListingTemplateOverride
              orgId={org!.id}
              listingId={id!}
              connectorId={l.connector_id}
              provider={connector?.provider ?? null}
            />

            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Channel selling price</h3>
              <p className="text-sm font-medium text-slate-800">{channelPriceSummary.label}</p>
              <p className="text-xs text-slate-500 mt-1">
                Channel-specific effective price. Product Master remains {formatStoredMoney(p?.selling_price, l.currency)}.
              </p>
              {channelPriceSummary.overrideCount > 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  {channelPriceSummary.overrideCount === 1 ? "Variant override" : `${channelPriceSummary.overrideCount} variant overrides`}
                </p>
              )}
              {channelPriceSummary.pendingLabel && (
                <p className="text-xs text-amber-700 mt-1">{channelPriceSummary.pendingLabel}</p>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Readiness</h3>
              {data.readiness ? (
                <div className="space-y-2">
                  <div className={`text-sm font-semibold ${data.readiness.status === "READY" ? "text-emerald-700" : data.readiness.status === "READY_WITH_WARNINGS" ? "text-amber-700" : "text-red-700"}`}>
                    {readinessLabel(data.readiness.status)}
                  </div>
                  <p className="text-xs text-slate-500">{data.readiness.blocking} blocking · {data.readiness.warning} warning{data.readiness.warning === 1 ? "" : "s"}</p>
                  <button onClick={() => { selectTab("readiness"); handleEvaluate(); }}
                    className="mt-2 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap">
                    Re-evaluate
                  </button>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Not evaluated</p>
              )}
            </div>

            {dirty.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Dirty Domains</h3>
                <div className="flex flex-wrap gap-2">
                  {dirty.map((d) => <span key={d} className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-full">{dirtyLabel[d]}</span>)}
                </div>
                <p className="text-xs text-slate-400 mt-2">{waitingToSyncCopy(dirty) || "Canonical changes detected."}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Variants */}
      {activeTab === "variants" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Variants ({data.variants.filter((v) => v.included).length} of {data.variants.length})</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => setVariantPage(Math.max(0, variantPage - 1))} disabled={variantPage === 0}
                className="px-2 py-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs rounded-lg disabled:opacity-40">Prev</button>
              <span className="text-xs text-slate-500">Page {variantPage + 1}</span>
              <button onClick={() => setVariantPage(variantPage + 1)} disabled={data.variants.length < variantPageSize}
                className="px-2 py-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs rounded-lg disabled:opacity-40">Next</button>
            </div>
          </div>
          {data.variants.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">No variants linked to this listing.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Included</th>
                    <th className="px-4 py-3 font-medium">SKU</th>
                    <th className="px-4 py-3 font-medium">Options</th>
                    <th className="px-4 py-3 font-medium">Canonical Price</th>
                    <th className="px-4 py-3 font-medium">Effective Channel Price</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.variants.map((v) => {
                    const opt = Object.entries(v.variant?.option_values ?? {}).map(([k, val]) => `${k}: ${val}`).join(", ");
                    const canonical = effectiveSellingPrice(v.variant?.selling_price, p?.selling_price);
                    const effectivePrice = resolveVariantEffectiveChannelPrice({
                      priceStrategy: l.price_strategy,
                      pricingConfig: pricingConfigFromProviderConfig(l.provider_config),
                      productSellingPrice: p?.selling_price,
                      variantSellingPrice: v.variant?.selling_price,
                      channelPrice: v.channel_price,
                    }).effective;
                    return (
                      <tr key={v.id} className={v.included ? "" : "opacity-50"}>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${v.included ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                            {v.included ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700">{v.variant?.sku ?? "—"}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{opt || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{formatStoredMoney(canonical, l.currency)}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatStoredMoney(effectivePrice, l.currency)}
                          {v.channel_price != null ? (
                            <span className="text-xs bg-amber-50 text-amber-700 px-1 py-0.5 rounded ml-1">Override</span>
                          ) : isInheritingPrice(l) ? (
                            <span className="text-xs bg-slate-100 text-slate-500 px-1 py-0.5 rounded ml-1">Inherited</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${v.variant?.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                            {v.variant?.status ?? "active"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {activeTab === "content" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h3 className="text-sm font-semibold text-slate-800">Content Overrides</h3>
          {[
            { label: "Title", field: "title" as ContentField, value: l.channel_title, inherited: p?.name },
            { label: "Description", field: "description" as ContentField, value: l.channel_description, inherited: p?.description },
            { label: "Short Description", field: "short_description" as ContentField, value: l.channel_short_description, inherited: null },
            { label: "Subtitle", field: "subtitle" as ContentField, value: l.channel_subtitle, inherited: null },
            { label: "Condition Description", field: "condition_description" as ContentField, value: l.channel_condition_description, inherited: p?.condition },
          ].map((item) => {
            const inheriting = isInheritingContent(l, item.field);
            const editing = contentEdit?.field === item.field;
            const displayValue = item.value || item.inherited || "—";

            return (
              <div key={item.label} className="border border-slate-100 rounded-lg p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-medium text-slate-500">{item.label}</span>

                  <span className="flex items-center gap-2">
                    {inheriting ? (
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">Inherited</span>
                    ) : (
                      <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">Override</span>
                    )}

                    {canManage && canEdit(l.status) && !editing && (
                      <>
                        <button
                          type="button"
                          disabled={actionBusy}
                          className="text-xs font-medium text-emerald-700 disabled:opacity-50"
                          onClick={() => setContentEdit({
                            field: item.field,
                            value: String(item.value ?? item.inherited ?? ""),
                          })}
                        >
                          {inheriting ? "Create Override" : "Edit"}
                        </button>

                        {!inheriting && (
                          <button
                            type="button"
                            disabled={actionBusy}
                            className="text-xs font-medium text-slate-600 disabled:opacity-50"
                            onClick={() => resetContentToInherited(item.field)}
                          >
                            {item.inherited != null ? "Use Product Master" : "Clear Override"}
                          </button>
                        )}
                      </>
                    )}
                  </span>
                </div>

                {editing ? (
                  <div className="mt-2 space-y-2">
                    {item.field === "description" || item.field === "condition_description" ? (
                      <textarea
                        value={contentEdit.value}
                        onChange={(e) => setContentEdit({ field: item.field, value: e.target.value })}
                        rows={item.field === "description" ? 8 : 4}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                      />
                    ) : (
                      <input
                        value={contentEdit.value}
                        onChange={(e) => setContentEdit({ field: item.field, value: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                      />
                    )}

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={actionBusy || !contentEdit.value.trim()}
                        onClick={saveContentOverride}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg"
                      >
                        {actionBusy ? "Saving..." : "Save override"}
                      </button>
                      <button
                        type="button"
                        disabled={actionBusy}
                        onClick={() => setContentEdit(null)}
                        className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{displayValue}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pricing */}
      {activeTab === "pricing" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h3 className="text-sm font-semibold text-slate-800">Pricing Strategy</h3>
          <p className="text-xs text-slate-500">Canonical product price stays authoritative. Price-only provider sync runs automatically for published listings. Content is not rewritten.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Rule</label>
              <select value={priceRule} onChange={(e) => setPriceRule(e.target.value as PriceStrategy)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                <option value="inherit">Same as base</option>
                <option value="fixed_override">Fixed price</option>
                <option value="fixed_markup">+$ / −$ amount</option>
                <option value="percentage_markup">+% / −% percentage</option>
              </select>
            </div>
            {priceRule !== "inherit" && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Amount (use negative for −$ / −%)</label>
                <input value={priceValue} onChange={(e) => setPriceValue(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder={priceRule === "percentage_markup" ? "10" : "1.00"} />
              </div>
            )}
          </div>
          {(() => {
            const draftCfg = priceFormConfig(priceRule, priceValue);
            const base = Number(p?.selling_price ?? 0);
            const intended = computeChannelPrice(priceRule, base, draftCfg);
            const symbol = currencySymbol(l.currency);
            return (
              <div className="text-sm text-slate-700 space-y-1">
                <p>Canonical base {formatMoneyPreview(Number.isFinite(base) && base > 0 ? base : null, symbol)}</p>
                <p>{formatPriceAdjustmentLine(priceRule, draftCfg)}</p>
                <p>Rule preview (before listing-variant overrides) {formatMoneyPreview(intended, symbol)}</p>
              </div>
            );
          })()}
          {canManage && (
            <button
              type="button"
              onClick={async () => {
                setActionBusy(true);
                setActionError("");
                try {
                  const { error: rpcErr } = await supabase.rpc("set_listing_pricing_rule", {
                    p_org_id: org!.id,
                    p_listing_id: id,
                    p_strategy: priceRule,
                    p_value: priceValue === "" ? 0 : Number(priceValue),
                  });
                  if (rpcErr) throw rpcErr;
                  queryClient.invalidateQueries({ queryKey: ["listing-detail", org?.id, id] });
                } catch (err) {
                  setActionError((err as Error).message);
                }
                setActionBusy(false);
              }}
              className="px-3 py-2 bg-emerald-600 text-white text-xs font-medium rounded-lg"
            >
              Save rule (does not publish)
            </button>
          )}
          <p className="text-xs text-slate-400">
            Current stored strategy: {l.price_strategy.replace(/_/g, " ")} · effective {channelPriceSummary.label}
            {channelPriceSummary.overrideCount > 0 ? " · variant override in effect" : ""}
          </p>
          {channelPriceSummary.pendingLabel && (
            <p className="text-xs text-amber-700">{channelPriceSummary.pendingLabel}</p>
          )}
          <div className="overflow-x-auto border border-slate-100 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Variant</th>
                  <th className="px-3 py-2 font-medium">SKU</th>
                  <th className="px-3 py-2 font-medium">Canonical</th>
                  <th className="px-3 py-2 font-medium">Channel rule</th>
                  <th className="px-3 py-2 font-medium">Listing override</th>
                  <th className="px-3 py-2 font-medium">Effective channel price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {priceRows.map((row) => (
                  <tr key={row.variantId}>
                    <td className="px-3 py-2 text-slate-700">{row.options}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{row.sku ?? "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{formatStoredMoney(row.canonicalBase, l.currency)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{row.strategyAdjustment}</td>
                    <td className="px-3 py-2 text-slate-600">{formatStoredMoney(row.listingOverride, l.currency)}</td>
                    <td className="px-3 py-2 text-slate-800">
                      {formatStoredMoney(row.effective, l.currency)}
                      <span className={`ml-1 text-[11px] px-1 py-0.5 rounded ${row.kind === "override" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                        {variantChannelPriceKindLabel(row.kind)}
                      </span>
                      <VariantChannelPriceControl
                        row={row}
                        role={role}
                        currency={l.currency}
                        channelName={connectorDisplayName({
                          id: connector?.id ?? "",
                          name: connector?.name ?? "",
                          provider: connector?.provider ?? null,
                        })}
                        priceStrategy={l.price_strategy}
                        pricingConfig={pricingConfigFromProviderConfig(l.provider_config)}
                        busy={actionBusy}
                        onCommit={handleListingVariantChannelPrice}
                        onError={setActionError}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inventory */}
      {activeTab === "inventory" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h3 className="text-sm font-semibold text-slate-800">Inventory Strategy</h3>
          <div className="border border-slate-100 rounded-lg p-3">
            <p className="text-xs text-slate-500">Strategy</p>
            <p className="text-sm font-medium text-slate-800 capitalize">{l.inventory_strategy.replace(/_/g, " ")}</p>
          </div>
          {hasDirtyDomain(l, "inventory") && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600">
              Inventory has changed. This listing will be updated on next publish.
            </div>
          )}
        </div>
      )}

      {activeTab === "media" && (
        <ListingMediaPanel
          orgId={org!.id}
          listingId={id!}
          listing={l}
          media={data.media}
          gallery={data.gallery}
          canManage={canManage}
          actionBusy={actionBusy}
          setActionBusy={setActionBusy}
          setActionError={setActionError}
        />
      )}

      {/* Readiness */}
      {activeTab === "readiness" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Readiness Evaluation</h3>
            <button onClick={handleEvaluate} disabled={readinessLoading}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap">
              {readinessLoading ? "Evaluating..." : "Evaluate now"}
            </button>
          </div>
          {(readinessData || data.readiness) ? (
            <div className="space-y-3">
              {(() => {
                const r = readinessData ?? data.readiness!;
                return (
                  <>
                    <div className={`text-lg font-semibold ${r.status === "READY" ? "text-emerald-700" : r.status === "READY_WITH_WARNINGS" ? "text-amber-700" : "text-red-700"}`}>
                      {readinessLabel(r.status)}
                    </div>
                    {readinessConnectorLine(r) && (
                      <p className="text-sm text-slate-700">{readinessConnectorLine(r)}</p>
                    )}
                    {!readinessConnectorLine(r) && connector && (
                      <p className="text-sm text-slate-700">{formatConnectorIdentityLine({
                        id: connector.id,
                        name: connector.name,
                        provider: connector.provider,
                        authStatus: connector.auth_status,
                        storefrontOrigin: data.storefront.originByConnector[l.connector_id],
                        storeHash: data.storefront.storeHashByConnector[l.connector_id],
                      })}</p>
                    )}
                    <p className="text-sm text-slate-600">{r.blocking} blocking · {r.warning} warning{r.warning === 1 ? "" : "s"}</p>
                    {r.checks.length > 0 && (
                      <div className="space-y-2 mt-3">
                        {r.checks.map((check, i) => (
                          <div key={i} className={`flex items-start gap-2 p-3 rounded-lg ${check.type === "blocking" ? "bg-red-50" : "bg-amber-50"}`}>
                            <i className={`${check.type === "blocking" ? "ri-close-circle-line text-red-500" : "ri-alert-line text-amber-500"} mt-0.5`} />
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${check.type === "blocking" ? "text-red-700" : "text-amber-700"}`}>{check.message}</p>
                              {readinessAction(check.code) && (
                                <p className="text-xs text-slate-600 font-medium mt-0.5">
                                  <i className="ri-arrow-right-line mr-1" />
                                  {readinessCheckHref(check) ? (
                                    <Link to={readinessCheckHref(check)!} className="underline">
                                      {readinessAction(check.code)}
                                    </Link>
                                  ) : (
                                    readinessAction(check.code)
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {r.checks.length === 0 && (
                      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-sm text-emerald-700">
                        <i className="ri-check-line mr-1" /> All content and marketplace data checks passed.
                        <p className="text-xs text-emerald-800/80 mt-1">
                          This covers data completeness only. Publication still requires approval, an authorized channel account, and no unresolved remote outcome.
                        </p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <p className="text-sm text-slate-500">Click "Evaluate now" to run the readiness check.</p>
          )}
        </div>
      )}

      {/* Sync History */}
      {activeTab === "sync" && (
        <SyncHistoryTab orgId={org!.id} listingId={id!} />
      )}

      {/* eBay Tab */}
      {activeTab === "ebay" && isEbay && (
        <EbayTab
          listingId={id!}
          connectorId={connector.id}
          orgId={org!.id}
          productId={l.product_id}
          categoryId={l.channel_category_id}
          categoryName={l.channel_category_name}
        />
      )}

      {/* BigCommerce Tab */}
      {activeTab === "bigcommerce" && isBigcommerce && (
        <BigcommerceTab
          listingId={id!}
          connectorId={connector.id}
          orgId={org!.id}
          categoryId={l.channel_category_id}
          categoryName={l.channel_category_name}
        />
      )}

      {/* Template Preview */}
      {activeTab === "preview" && (
        <RealListingPreview orgId={org!.id} listingId={id!} />
      )}

      {/* Publication Projection */}
      <Modal
        open={projection != null}
        onClose={() => setProjection(null)}
        title="Publication Projection"
        size="xl"
        scrollBody
      >
        <p className="text-xs text-slate-500 mb-3">
          Read-only preview of the current intended listing version. Nothing is sent to the marketplace from this window.
        </p>
        <pre className="text-xs text-slate-600 bg-slate-50 p-3 rounded-lg overflow-auto whitespace-pre-wrap break-words">
          {projection ? JSON.stringify(projection, null, 2) : ""}
        </pre>
      </Modal>

      {/* Archive Confirm */}
      <Modal open={archiveConfirm} onClose={() => setArchiveConfirm(false)} title="Archive this listing?"
        footer={
          <>
            <button onClick={() => setArchiveConfirm(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleArchive} disabled={actionBusy} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {actionBusy ? "Archiving..." : "Archive"}
            </button>
          </>
        }
      >
        <p className="text-sm text-slate-600">
          Archiving this listing will mark it as ended. If it is currently published on eBay, please end it on eBay first.
        </p>
      </Modal>
    </div>
  );
}

function SyncHistoryTab({ orgId, listingId }: { orgId: string; listingId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["sync-jobs", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data } = await supabase
        .from("sync_jobs")
        .select("id, job_type, status, error_message, error_class, attempts, max_attempts, idempotency_key, payload, result, created_at, started_at, completed_at")
        .eq("org_id", orgId)
        .eq("entity_type", "channel_listing")
        .eq("entity_id", listingId)
        .order("created_at", { ascending: false })
        .limit(20);
      return (data ?? []) as {
        id: string;
        job_type: string;
        status: string;
        error_message: string | null;
        error_class: string | null;
        attempts: number;
        max_attempts: number;
        idempotency_key: string | null;
        payload: Record<string, unknown> | null;
        result: Record<string, unknown> | null;
        created_at: string;
        started_at: string | null;
        completed_at: string | null;
      }[];
    },
  });

  if (isLoading) return <div className="p-4 text-center"><i className="ri-loader-4-line animate-spin text-slate-400" /></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-800">Sync History</h3>
      </div>
      {!data || data.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-slate-400">No sync jobs yet.</div>
      ) : (
        <div className="divide-y divide-slate-50">
          {data.map((job) => {
            const payload = job.payload ?? {};
            const result = job.result ?? {};
            const requested = (result.requested_domains as string[] | undefined) ?? (payload.domains as string[] | undefined) ?? (payload.domain ? [String(payload.domain)] : []);
            const provider = String(result.provider ?? payload.provider ?? "");
            const badge = listingSyncHistoryBadge(job.status);
            const diagnostic = publicationJobDiagnostic(job);
            return (
            <div key={job.id} className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{job.job_type.replace(/_/g, " ")}</p>
                <p className="text-xs text-slate-500">
                  {requested.length ? `Domains: ${requested.join(", ")}` : "Domains: —"}
                  {provider ? ` · ${provider}` : ""}
                </p>
                <p className="text-xs text-slate-400">
                  Queued {new Date(job.created_at).toLocaleString()}
                  {job.started_at ? ` · started ${new Date(job.started_at).toLocaleString()}` : ""}
                  {job.completed_at ? ` · finished ${new Date(job.completed_at).toLocaleString()}` : ""}
                  {job.attempts > 0 ? ` · ${job.attempts}/${job.max_attempts} attempts` : ""}
                </p>
                {job.error_message && (
                  <p className={`text-xs mt-0.5 ${diagnostic.tone === "warn" ? "text-amber-700" : "text-red-600"}`}>{job.error_message}</p>
                )}
                {diagnostic.classification && (
                  <p className="text-xs text-slate-600 mt-0.5">
                    <i className="ri-information-line mr-1" />
                    {diagnostic.classification}
                  </p>
                )}
                {diagnostic.detail && (
                  <p className="text-[11px] text-slate-400 mt-0.5 break-words">{diagnostic.detail}</p>
                )}
                {diagnostic.detailLost && (
                  <p className="text-[11px] text-amber-700 mt-0.5">{lostDetailNote()}</p>
                )}
              </div>
              <StatusBadge status={badge} customLabel={job.status.replace(/_/g, " ")} />
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}